import { Box, Typography, TextField, Button, Link, Alert, Divider, FormControlLabel, Checkbox } from '@mui/material'
import { useState, useEffect } from 'react'
import { Link as RouterLink, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useLoginMutation, useGetAuthConfigQuery } from '@store/api/authApi'
import { loginStart, loginSuccess, loginFailure } from '@store/slices/authSlice'
import GoogleOAuthButton from '@components/auth/GoogleOAuthButton'
import { resolvePostAuthPath } from '@utils/authRedirect'

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  })
  const [error, setError] = useState('')

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const [loginMutation, { isLoading: isLoginLoading }] = useLoginMutation()
  const { data: authConfig } = useGetAuthConfigQuery()
  const googleOAuthEnabled = authConfig?.googleOAuth ?? false

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(resolvePostAuthPath(location, searchParams), { replace: true })
    }
  }, [isAuthenticated, navigate, location, searchParams])

  // Show error if redirected back from a failed OAuth attempt
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const oauthError = params.get('error')
    if (oauthError === 'oauth_failed') {
      const message = params.get('message') || 'Google sign-in is not available. Please use email and password.'
      setError(message)
    }
  }, [location.search])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setFormData({
      ...formData,
      [e.target.name]: value,
    })
    // Clear error when user starts typing
    if (error) setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    try {
      dispatch(loginStart())
      const result = await loginMutation(formData).unwrap()
      
      dispatch(loginSuccess({
        user: result.user,
        token: result.accessToken,
        rememberMe: formData.rememberMe,
      }))
      
      navigate(resolvePostAuthPath(location, searchParams), { replace: true })
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || 'Login failed. Please try again.'
      setError(errorMessage)
      dispatch(loginFailure(errorMessage))
    }
  }

  const handleGoogleLogin = () => {
    // Generate state parameter for CSRF protection
    const array = new Uint8Array(16)
    crypto.getRandomValues(array)
    const state = Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('')
    sessionStorage.setItem('oauth_state', state)
    
    // Redirect to Google OAuth endpoint with state (relative path for nginx proxy)
    window.location.href = `/api/v1/auth/google?state=${state}`
  }

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto' }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Welcome Back
      </Typography>
      
      <Typography variant="body2" color="text.secondary" align="center" paragraph>
        Sign in to your CigarTrack account
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <TextField
          fullWidth
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          margin="normal"
          required
        />
        
        <TextField
          fullWidth
          label="Password"
          name="password"
          type="password"
          value={formData.password}
          onChange={handleChange}
          margin="normal"
          required
          inputProps={{
            minLength: 8
          }}
          helperText="Minimum 8 characters"
        />
        
        <FormControlLabel
          control={
            <Checkbox
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleChange}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body2">Keep me signed in</Typography>
              <Typography variant="caption" color="text.secondary">
                ⚠️ Stays logged in for 30 days. Only use on trusted devices.
              </Typography>
            </Box>
          }
          sx={{ mt: 1 }}
        />
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoginLoading}
          sx={{ mt: 3, mb: 2 }}
        >
          {isLoginLoading ? 'Signing In...' : 'Sign In'}
        </Button>
        
        {googleOAuthEnabled && (
          <>
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>

            <GoogleOAuthButton
              onClick={handleGoogleLogin}
              disabled={isLoginLoading}
              text="Sign in with Google"
            />
          </>
        )}

        <Box textAlign="center" sx={{ mb: 1 }}>
          <Link component={RouterLink} to="/auth/forgot-password">
            Forgot your password?
          </Link>
        </Box>
        
        <Box textAlign="center">
          <Link component={RouterLink} to="/auth/register">
            Don&apos;t have an account? Sign up
          </Link>
        </Box>
      </Box>
    </Box>
  )
}

export default Login
